<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { Sketch } from '@ckpack/vue-color';

const PALETA_PASTELES = [
  '#ffffff',
  '#cccccc',
  '#f8b3ba',
  '#f59598',
  '#f9a586',
  '#efa796',
  '#febd7d',
  '#cdb3d5',
  '#b3b4da',
  '#a6c7ea',
  '#8abade',
  '#689aab',
  '#a9dad1',
  '#90cfb5',
  '#88bbaa',
  '#bbdcad',
  '#bacab2',
  '#f9f08d',
  '#e1e78a',
  '#f1ed8c',
  '#f4f1ae',
  '#ccac90',
  '#fbdede',
];

const props = defineProps({
  name: { type: [String, Boolean], default: false },
  defaultColor: { type: String, default: null },
  disableAlpha: { type: Boolean, default: true },
  modelValue: { type: String, default: null },
  posicion: { type: String, default: 'derecha' },
  mostrarInput: { type: Boolean, default: true },
  coloresPredefinidos: { type: Array, default: null },
});

const emit = defineEmits(['update:modelValue']);

const colorpicker = ref(null);
const colors = ref({
  hex: props.defaultColor,
});
const colorValue = ref('');
const displayPicker = ref(false);

const clasesBotonColor = computed(() => {
  let clases = 'color-picker-container';
  if (props.mostrarInput) {
    clases = `${clases} input-group-addon`;
  } else {
    clases = `${clases} form-control`;
  }
  return clases;
});

const coloresPredefinidosFinales = computed(() => {
  if (props.coloresPredefinidos && props.coloresPredefinidos.length) {
    return props.coloresPredefinidos;
  }
  return PALETA_PASTELES;
});

const updateColors = (color) => {
  if (!color) {
    return;
  }

  if (color.slice(0, 1) === '#') {
    colors.value = {
      hex: color,
    };
  } else if (color.slice(0, 4) === 'rgba') {
    // Soporte para un valor rgba(...)
    const rgba = color.replace(/^rgba?\(|\s+|\)$/g, '').split(',');
    const hex = '#' + ((1 << 24) + (parseInt(rgba[0]) << 16) + (parseInt(rgba[1]) << 8) + parseInt(rgba[2])).toString(16).slice(1);
    colors.value = {
      hex,
      a: rgba[3],
    };
  }
};

const setColor = (color) => {
  updateColors(color);
  colorValue.value = color;
};

const showPicker = () => {
  document.addEventListener('click', documentClick);
  displayPicker.value = true;
};

const hidePicker = () => {
  document.removeEventListener('click', documentClick);
  displayPicker.value = false;
};

const togglePicker = () => {
  if (displayPicker.value) {
    hidePicker();
  } else {
    showPicker();
  }
};

const updateFromInput = () => {
  updateColors(colorValue.value);
};

const updateFromPicker = (color) => {
  colors.value = color;
  colorValue.value = color.hex;
};

const documentClick = (e) => {
  const el = colorpicker.value;
  const { target } = e;
  if (el !== target && !el.contains(target)) {
    hidePicker();
  }
};

watch(
  () => colorValue.value,
  (val) => {
    if (val) {
      updateColors(val);
      emit('update:modelValue', val);
    }
  },
);

watch(
  () => props.modelValue,
  (val) => {
    setColor(val || props.defaultColor);
  },
);

onMounted(() => {
  setColor(props.modelValue || props.defaultColor);
});
</script>
<template>
  <div ref="colorpicker" class="input-group color-picker">
    <span
      v-if="posicion === 'izquierda'"
      :class="clasesBotonColor"
      @click="togglePicker()"
    >
      <span
        class="current-color"
        :style="'background-color: ' + colorValue"
      >
      </span>
    </span>
    <input
      v-show="mostrarInput"
      v-model="colorValue"
      type="text"
      :name="name"
      class="form-control"
      @focus="showPicker()"
      @input="updateFromInput"
    />
    <span
      v-if="posicion !== 'izquierda'"
      :class="clasesBotonColor"
      @click="togglePicker()"
    >
      <span
        class="current-color"
        :style="'background-color: ' + colorValue"
      >
      </span>
    </span>
    <Sketch
      v-if="displayPicker"
      :model-value="colors"
      :class="{izquierda: posicion === 'izquierda'}"
      :disable-alpha="disableAlpha"
      :preset-colors="coloresPredefinidosFinales"
      @update:model-value="updateFromPicker"
    />
  </div>
</template>

<style lang="scss" scoped>
  h1 {
    height: 120px;
    line-height: 120px;
    text-align: center;
  }
  .vc-chrome,
  .vc-sketch {
    position: absolute;
    top: 35px;
    right: 0;
    z-index: 9;
    &.izquierda {
      left: 0;
    }
  }
  .color-picker-container {
    cursor: pointer;
  }

  .current-color {
    display: inline-block;
    width: 16px;
    height: 16px;
    // background-color: #ccc;
  }
  .footer {
    margin-top: 20px;
    text-align: center;
  }
</style>
